import * as React from "react";
import { Upload } from "antd";

import "../../styling/antd.scss";

import { createVideoThumbnailBlob, saveElementToFirebaseCancellable } from "@/utils";
import { ConfigContext } from "@/contexts/appContexts";
import { SocialFiles } from "@/interfaces";
import { Button } from "@/components/ui";
import { UploadIcon } from "lucide-react";

interface OwnProps {
  onChangeImage: (file: SocialFiles) => void;
  fileName: string;
  title: string;
  customMessage?: string;
  nodeName?: string;
  accept?: string;
  placeholder?: string;
  multiple?: boolean;
  secondary?: boolean;
}

type Props = OwnProps;

export const UploadMultipleElements: React.FC<Props> = ({
  nodeName = "bulk",
  accept = "image/png, image/jpeg, video/mp4",
  customMessage = "Drag and drop your files here to upload",
  fileName,
  title,
  multiple = true,
  secondary = false,
  onChangeImage,
}) => {
  const { firebase: firebaseConfig } = React.useContext(ConfigContext);

  const props = {
    name: "file",
    multiple,
    showUploadList: false,
  };

  const timestamp = new Date().getTime();
  const fileNode = `${fileName}/${nodeName}${timestamp}`;
  return (
    <div className="w-full flex flex-col items-center justify-center">
      <Upload
        {...props}
        data-elm-id="fileElementUpload"
        openFileDialogOnClick={true}
        accept={accept}
        customRequest={({ file, onSuccess }) => {
          const scFile = file as SocialFiles;
          const firebasePromise = saveElementToFirebaseCancellable(firebaseConfig, fileNode, scFile, `${scFile.uid}`);
          const mediaType = scFile.type.includes("video") ? "VIDEO" : "IMAGE";
          const thumbnail = URL.createObjectURL(scFile);
          const urlPromise = mediaType === "IMAGE" ? Promise.resolve(thumbnail) : createVideoThumbnailBlob(thumbnail);
          urlPromise.then(url => {
            scFile.thumbnail = url;
            scFile.uploadTask = firebasePromise;
            onChangeImage(scFile);
            onSuccess?.("ok");
          });
        }}
        className="w-full"
      >
        <div className="flex flex-row items-center justify-between p-4 px-6 border-2 border-dashed border-slate-300 rounded-xl bg-slate-50/50 hover:bg-slate-50 transition-colors gap-4 w-full cursor-pointer select-none">
          <div className="flex items-center gap-3">
            <div className="bg-white p-2 rounded-full shadow-xs border border-slate-100 flex items-center justify-center">
              <UploadIcon className="text-slate-400 size-5" />
            </div>
            <div className="text-sm text-slate-500 text-left whitespace-pre-line font-medium">{customMessage}</div>
          </div>

          <div className="flex items-center gap-4 shrink-0">
            <span className="text-xs text-slate-400 font-semibold uppercase select-none">or</span>
            <Button
              data-elm-id={`uploadModal${title.replace(" ", "")}Btn`}
              variant={secondary ? "outline" : "default"}
              type="button"
              className="font-semibold text-sm px-4 h-9"
            >
              {title}
            </Button>
          </div>
        </div>
      </Upload>
    </div>
  );
};
